import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, ref, watch } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

export type MaskPreset = "custom" | "cmnd" | "cccd" | "phone" | "tax" | "bank";

interface Props {
  modelValue?: string;
  /** Preset sẵn: cmnd | cccd | phone | tax | bank | custom */
  preset?: MaskPreset;
  /**
   * Pattern mask:
   * - # = chữ số
   * - A = chữ cái
   * - * = chữ hoặc số
   * - ký tự khác giữ nguyên (vd: khoảng trắng, gạch)
   * Ví dụ: "#### #### ####" hoặc "###-###-####"
   */
  mask?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  status?: "" | "error" | "warning";
  /** true = emit raw (không có ký tự mask), false = emit chuỗi đã mask */
  unmasked?: boolean;
  prefixIcon?: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MaskedInput',
  props: {
    modelValue: { default: "" },
    preset: { default: "custom" },
    mask: { default: "" },
    placeholder: { default: "" },
    disabled: { type: Boolean, default: false },
    size: { default: "middle" },
    allowClear: { type: Boolean, default: true },
    status: { default: "" },
    unmasked: { type: Boolean, default: true },
    prefixIcon: { default: "" }
  },
  emits: ["update:modelValue", "blur", "pressEnter", "change"],
  setup(__props: any, { emit: __emit }) {

const PRESETS: Record<Exclude<MaskPreset, "custom">, { mask: string; placeholder: string; icon: string }> = {
  cmnd: {
    mask: "#########",
    placeholder: "Nhập CMND (9 số)",
    icon: "fas fa-id-card",
  },
  cccd: {
    mask: "############",
    placeholder: "Nhập CCCD (12 số)",
    icon: "fas fa-id-card",
  },
  phone: {
    mask: "#### ### ###",
    placeholder: "0xxx xxx xxx",
    icon: "fas fa-phone",
  },
  tax: {
    mask: "##########-###",
    placeholder: "Mã số thuế",
    icon: "fas fa-file-invoice",
  },
  bank: {
    mask: "#### #### #### ####",
    placeholder: "Số tài khoản / thẻ",
    icon: "fas fa-credit-card",
  },
};

const props = __props;

const emit = __emit;

const displayValue = ref("");

const activeMask = computed(() => {
  if (props.preset !== "custom" && PRESETS[props.preset]) {
    return PRESETS[props.preset].mask;
  }
  return props.mask || "";
});

const resolvedPlaceholder = computed(() => {
  if (props.placeholder) return props.placeholder;
  if (props.preset !== "custom" && PRESETS[props.preset]) {
    return PRESETS[props.preset].placeholder;
  }
  return "";
});

const resolvedPrefixIcon = computed(() => {
  if (props.prefixIcon) return props.prefixIcon;
  if (props.preset !== "custom" && PRESETS[props.preset]) {
    return PRESETS[props.preset].icon;
  }
  return "";
});

const maxDisplayLength = computed(() => activeMask.value.length || undefined);

const isMaskToken = (char: string) => char === "#" || char === "A" || char === "*";

const matchToken = (token: string, char: string) => {
  if (token === "#") return /\d/.test(char);
  if (token === "A") return /[a-zA-Z]/.test(char);
  if (token === "*") return /[a-zA-Z0-9]/.test(char);
  return false;
};

/** Lấy chuỗi raw theo thứ tự token trong mask */
const extractRaw = (value: string): string => {
  const mask = activeMask.value;
  if (!mask) return value;

  const tokens = mask.split("").filter(isMaskToken);
  let raw = "";

  for (const ch of value) {
    if (raw.length >= tokens.length) break;
    if (matchToken(tokens[raw.length], ch)) {
      raw += ch;
    }
  }

  return raw;
};

const applyMask = (raw: string): { masked: string; unmasked: string } => {
  const mask = activeMask.value;
  if (!mask) {
    return { masked: raw, unmasked: raw };
  }

  let masked = "";
  let unmasked = "";
  let rawIndex = 0;

  for (let i = 0; i < mask.length; i++) {
    const token = mask[i];

    if (!isMaskToken(token)) {
      if (rawIndex < raw.length) {
        masked += token;
      }
      continue;
    }

    if (rawIndex >= raw.length) break;

    const ch = raw[rawIndex];
    if (!matchToken(token, ch)) break;

    masked += ch;
    unmasked += ch;
    rawIndex++;
  }

  return { masked, unmasked };
};

watch(
  () => [props.modelValue, activeMask.value] as const,
  ([val]) => {
    const source = String(val ?? "");
    const raw = props.unmasked ? extractRaw(source) : extractRaw(source);
    displayValue.value = applyMask(raw).masked;
  },
  { immediate: true }
);

const onInput = (val: string) => {
  const raw = extractRaw(val);
  const { masked, unmasked } = applyMask(raw);
  displayValue.value = masked;
  const emitValue = props.unmasked ? unmasked : masked;
  emit("update:modelValue", emitValue);
  emit("change", emitValue, masked);
};

const onBlur = (e: FocusEvent) => emit("blur", e);
const onPressEnter = (e: KeyboardEvent) => emit("pressEnter", e);

return (_ctx: any,_cache: any) => {
  const _component_a_input = _resolveComponent("a-input")!

  return (_openBlock(), _createBlock(_component_a_input, {
    value: displayValue.value,
    "onUpdate:value": [
      _cache[0] || (_cache[0] = ($event: any) => ((displayValue).value = $event)),
      onInput
    ],
    placeholder: resolvedPlaceholder.value,
    disabled: _ctx.disabled,
    size: _ctx.size,
    "allow-clear": _ctx.allowClear,
    maxlength: maxDisplayLength.value,
    status: _ctx.status,
    class: "ant-masked-input",
    onBlur: onBlur,
    onPressEnter: onPressEnter
  }, _createSlots({ _: 2 }, [
    (resolvedPrefixIcon.value)
      ? {
          name: "prefix",
          fn: _withCtx(() => [
            _createElementVNode("i", {
              class: _normalizeClass(resolvedPrefixIcon.value)
            }, null, 2)
          ]),
          key: "0"
        }
      : undefined
  ]), 1032, ["value", "placeholder", "disabled", "size", "allow-clear", "maxlength", "status"]))
}
}

})